import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import {
  useChargingKeyAssignChargingKey,
  useChargingKeyGetChargingKeys,
  useChargingKeySetChargingKeyEnabled,
  useChargingKeyUnassignChargingKey,
} from 'api/charging-key/charging-key';
import { usePaymentCardGetPaymentCard } from 'api/payment-card/payment-card';
import Modal from 'components/Modal';
import SectionHeader from 'components/SectionHeader';
import UserPicker from 'components/UserPicker';
import LabelWrap from 'components/inputs/LabelWrap';
import TextButton from 'components/inputs/TextButton';
import Roles from 'constants/Roles';
import useMe from 'contexts/authentication/useMe';
import useModalStack from 'contexts/modal/useModalStack';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { chargingKeyLabel, toastChargingKeyResult } from './chargingKeyUtils';
import ExternalReceipstList from './ExternalReceipstList';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 320px;
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  margin: 0;

  dt {
    font-weight: bold;
  }
  dd {
    margin: 0;
  }
`;

const Hint = styled.p`
  font-size: 12px;
  font-style: italic;
  margin: 0;
`;

interface Props {
  paymentCardId: number;
  onUpdated?: () => void;
}

const ChargingKeyModal: FC<Props> = ({ paymentCardId, onUpdated }) => {
  const modalStack = useModalStack();
  const queryClient = useQueryClient();
  const me = useMe();
  const canEdit = me?.roles.includes(Roles.CircleK);

  const chargingKeys = useChargingKeyGetChargingKeys();
  // Sessionerna är kvitton på nyckelns PaymentCard — samma lista som betalkortsmodalen visar
  const paymentCard = usePaymentCardGetPaymentCard({ id: paymentCardId });
  const key = chargingKeys.data?.keys.find(
    (k) => k.paymentCardId === paymentCardId
  );
  const integrationEnabled = chargingKeys.data?.integrationEnabled ?? false;

  const [cardHolderId, setCardHolderId] = useState<number | null>(null);
  useEffect(() => {
    setCardHolderId(key?.cardHolderID ?? null);
  }, [key?.cardHolderID]);

  const refresh = async () => {
    await queryClient.invalidateQueries(chargingKeys.queryKey);
    onUpdated?.();
  };

  const assignCall = useChargingKeyAssignChargingKey();
  const unassignCall = useChargingKeyUnassignChargingKey();
  const setEnabledCall = useChargingKeySetChargingKeyEnabled();
  const isBusy =
    assignCall.isLoading || unassignCall.isLoading || setEnabledCall.isLoading;

  const saveHolder = async () => {
    if (!key) return;
    try {
      if (cardHolderId === null) {
        const result = await unassignCall.mutateAsync({
          data: { paymentCardId },
        });
        toastChargingKeyResult(
          result,
          `${chargingKeyLabel(key)} är inte längre tilldelad`
        );
      } else {
        const result = await assignCall.mutateAsync({
          data: { paymentCardId, cardHolderID: cardHolderId },
        });
        toastChargingKeyResult(
          result,
          `${chargingKeyLabel(key)} är tilldelad ${result.key.cardHolderName}${
            !key.isActive && result.key.isActive
              ? ' och påslagen hos Fortum'
              : ''
          }`
        );
      }
      await refresh();
      modalStack.pop();
    } catch {
      // Felet visas som toast av den globala felhanteringen
    }
  };

  const toggleEnabled = async () => {
    if (!key) return;
    try {
      const result = await setEnabledCall.mutateAsync({
        data: { paymentCardId, enabled: !key.isActive },
      });
      toastChargingKeyResult(
        result,
        result.key.isActive
          ? `${chargingKeyLabel(key)} är påslagen hos Fortum`
          : `${chargingKeyLabel(key)} är avstängd hos Fortum`
      );
      await refresh();
    } catch {
      // Felet visas som toast av den globala felhanteringen
    }
  };

  const holderChanged = key !== undefined && cardHolderId !== (key.cardHolderID ?? null);

  return (
    <Modal
      title="Laddnyckel"
      buttons={[
        ...(canEdit
          ? [
              {
                label: isBusy ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Spara',
                onClick: saveHolder,
                disabled: isBusy || !holderChanged,
              },
            ]
          : []),
        {
          label: canEdit ? 'Avbryt' : 'Stäng',
          onClick: () => modalStack.pop(),
        },
      ]}
    >
      <Wrapper>
        {!key ? (
          <FontAwesomeIcon icon={faSpinner} spin />
        ) : (
          <>
            <SectionHeader>Fortum Charge &amp; Drive</SectionHeader>
            <Facts>
              <dt>Nummer</dt>
              <dd>{key.visualNumber ?? '–'}</dd>
              <dt>UID</dt>
              <dd>{key.uid}</dd>
              <dt>Status</dt>
              <dd>{key.isActive ? 'Påslagen' : 'Avstängd'}</dd>
            </Facts>
            <LabelWrap label="Fälttestare">
              <UserPicker
                disabled={!canEdit || isBusy}
                selectedUserId={cardHolderId}
                onUserPicked={(user) => setCardHolderId(user?.userID ?? null)}
                noneOption="Ingen"
              />
            </LabelWrap>
            {canEdit &&
              integrationEnabled &&
              !key.isActive &&
              holderChanged &&
              cardHolderId !== null && (
                <Hint>Nyckeln slås på hos Fortum när du sparar.</Hint>
              )}
            {canEdit && integrationEnabled && (
              <TextButton
                style={{ alignSelf: 'flex-start' }}
                disabled={isBusy}
                onClick={toggleEnabled}
              >
                {key.isActive
                  ? 'Stäng av nyckeln hos Fortum'
                  : 'Slå på nyckeln hos Fortum'}
              </TextButton>
            )}
            <SectionHeader>Laddsessioner</SectionHeader>
            <ExternalReceipstList paymentCard={paymentCard.data} />
          </>
        )}
      </Wrapper>
    </Modal>
  );
};

export default ChargingKeyModal;
